<template>
  <div
    v-if="showOverlay"
    class="demo-overlay"
  >
    <!-- Semi-transparent backdrop -->
    <div
      class="demo-overlay__backdrop"
      @click="closeOverlay"
    />

    <!-- Highlight box and tooltip -->
    <transition
      name="demo-overlay-content"
      @enter="onEnter"
      @leave="onLeave"
    >
      <div
        v-if="hasTargetElement"
        key="overlay-content"
        class="demo-overlay__container"
        :style="containerStyle"
      >
        <!-- Highlight box (drawn via canvas or CSS) -->
        <div
          class="demo-overlay__highlight"
          :style="highlightStyle"
        />

        <!-- Tooltip with instructions -->
        <div
          class="demo-overlay__tooltip"
          :class="`demo-overlay__tooltip--${tooltipPosition}`"
          :style="tooltipStyle"
          role="tooltip"
          aria-live="polite"
        >
          <!-- Arrow pointer -->
          <div
            class="demo-overlay__arrow"
            :style="arrowStyle"
          />

          <!-- Tooltip content -->
          <div class="demo-overlay__content">
            <slot name="content">
              <p class="demo-overlay__text">
                {{ content }}
              </p>
            </slot>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import {
  getElementBoundsWithPadding,
  calculateTooltipPosition,
  getViewportDimensions,
  isMobileViewport,
} from '@/demo/services/demoService';
import { OVERLAY, ANIMATION, TOOLTIP } from '@/demo/constants';

/**
 * DemoOverlay Component
 * Renders semi-transparent backdrop with highlight box around target element
 * and positioning tooltip with instructions
 * 
 * @component
 * @example
 * <DemoOverlay 
 *   :targetElement="element"
 *   :position="'bottom'"
 *   :padding="8"
 *   content="Click on this element to continue"
 * />
 */
export default {
  name: 'DemoOverlay',

  props: {
    /**
     * Target DOM element to highlight
     */
    targetElement: {
      type: HTMLElement,
      default: null,
    },

    /**
     * Preferred tooltip position (top, bottom, left, right, auto)
     */
    position: {
      type: String,
      default: 'bottom',
      validator: (value) => ['top', 'bottom', 'left', 'right', 'auto'].includes(value),
    },

    /**
     * Padding around highlight box in pixels
     */
    padding: {
      type: Number,
      default: OVERLAY.HIGHLIGHT?.PADDING || 8,
    },

    /**
     * Content text to display in tooltip
     */
    content: {
      type: String,
      default: '',
    },

    /**
     * Whether overlay is visible
     */
    show: {
      type: Boolean,
      default: true,
    },

    /**
     * Custom z-index for overlay
     */
    zIndex: {
      type: Number,
      default: OVERLAY.BACKDROP_Z_INDEX || 998,
    },

    /**
     * Allow closing overlay by clicking backdrop
     */
    closeOnBackdropClick: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['close', 'highlight-change'],

  data() {
    return {
      showOverlay: true,
      elementBounds: null,
      viewportDimensions: null,
      resizeObserver: null,
      highlightWatch: null,
    };
  },

  computed: {
    /**
     * Check if target element exists
     */
    hasTargetElement() {
      return this.targetElement && this.elementBounds;
    },

    /**
     * Single source-of-truth for tooltip placement — position label + coordinates.
     */
    tooltipData() {
      if (!this.elementBounds || !this.targetElement) {
        return { position: 'bottom', top: 0, left: 0 };
      }
      return calculateTooltipPosition(this.targetElement, this.position);
    },

    /**
     * CSS-class suffix driven by the resolved tooltip position.
     */
    tooltipPosition() {
      return this.tooltipData.position;
    },

    /**
     * Shift the arrow so it always points at the element centre,
     * even when the tooltip box has been clamped to the viewport edge.
     */
    arrowStyle() {
      if (!this.elementBounds) return {};
      const { position, top, left } = this.tooltipData;

      // For top/bottom tooltips the box may be horizontally clamped to the
      // viewport edge, so the CSS `left: 50%` arrow would no longer point at
      // the element centre.  Calculate the exact pixel offset to correct it.
      if (position === 'top' || position === 'bottom') {
        const mobile = isMobileViewport();
        const tooltipW = mobile
          ? Math.round(TOOLTIP.MAX_WIDTH * 0.85)
          : TOOLTIP.MAX_WIDTH;
        // elementBounds includes padding on each side, so subtract it to get
        // the true element centre (otherwise the arrow is shifted right by padding px).
        const elemCenterX = this.elementBounds.left + this.elementBounds.width / 2 - this.padding;
        const rawLeft = elemCenterX - left - 6;
        const clamped = Math.max(10, Math.min(rawLeft, tooltipW - 22));
        return { left: `${clamped}px`, marginLeft: '0' };
      }

      // For left/right tooltips, calculateTooltipPosition already vertically
      // centres the tooltip on the element, so CSS `top: 50%; margin-top: -6px`
      // points the arrow at the element centre correctly — no JS override needed.
      return {};
    },

    /**
     * Calculate highlight box style
     */
    highlightStyle() {
      if (!this.elementBounds) return {};

      return {
        position: 'fixed',
        top: `${this.elementBounds.top}px`,
        left: `${this.elementBounds.left}px`,
        width: `${this.elementBounds.width}px`,
        height: `${this.elementBounds.height}px`,
        border: `${OVERLAY.HIGHLIGHT?.BORDER_WIDTH || 3}px ${OVERLAY.HIGHLIGHT?.BORDER_STYLE || 'solid'} ${OVERLAY.HIGHLIGHT?.BORDER_COLOR || '#FFD700'}`,
        borderRadius: '4px',
        boxShadow: OVERLAY.HIGHLIGHT?.BOX_SHADOW || '0 0 20px rgba(255, 215, 0, 0.6)',
        pointerEvents: 'none',
        zIndex: OVERLAY.HIGHLIGHT_Z_INDEX || 999,
        animation: 'demo-highlight-pulse 2s ease-in-out infinite',
        transition: `all ${ANIMATION.DURATION}ms ${ANIMATION.EASING}`,
      };
    },

    /**
     * Calculate container style for highlight
     */
    containerStyle() {
      return {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
        zIndex: OVERLAY.HIGHLIGHT_Z_INDEX || 999,
      };
    },

    /**
     * Calculate tooltip style based on position
     */
    tooltipStyle() {
      if (!this.elementBounds) return {};

      const { top, left, verticalCenter } = this.tooltipData;
      const mobile = isMobileViewport();

      return {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        ...(verticalCenter ? { transform: 'translateY(-50%)' } : {}),
        maxWidth: mobile
          ? `${Math.round(TOOLTIP.MAX_WIDTH * 0.85)}px`
          : `${TOOLTIP.MAX_WIDTH}px`,
        minWidth: `${TOOLTIP.MIN_WIDTH}px`,
        zIndex: OVERLAY.TOOLTIP_Z_INDEX || 1000,
        pointerEvents: 'auto',
      };
    },
  },

  watch: {
    /**
     * Watch for show prop changes
     */
    show(newVal) {
      this.showOverlay = newVal;
    },

    /**
     * Update when target element changes
     */
    targetElement(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.updateElementBounds();
        this.$emit('highlight-change', newVal);
      }
    },


  },

  mounted() {
    // Inject animation styles
    this.injectAnimationStyles();

    // Initial bounds calculation
    this.updateElementBounds();

    // Setup resize observer to track element position changes
    this.setupResizeObserver();

    // Setup window resize listener
    window.addEventListener('resize', this.handleWindowResize);

    // Make overlay visible
    this.showOverlay = this.show;
  },

  beforeUnmount() {
    this.cleanup();
  },

  methods: {
    /**
     * Update element bounds and tooltip position
     */
    updateElementBounds() {
      if (!this.targetElement) {
        this.elementBounds = null;
        return;
      }

      this.elementBounds = getElementBoundsWithPadding(this.targetElement, this.padding);

      // Scroll element into view if needed
      if (this.elementBounds) {
        this.ensureElementInViewport();
      }
    },

    /**
     * Ensure element is visible in viewport
     */
    ensureElementInViewport() {
      if (!this.targetElement || this.isElementInViewport()) {
        return;
      }

      // For iframe elements, scroll the iframe first
      let ownerDoc = this.targetElement.ownerDocument;
      if (ownerDoc !== document) {
        const iframes = document.querySelectorAll('iframe');
        for (const iframe of iframes) {
          try {
            if (iframe.contentDocument === ownerDoc) {
              iframe.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              });
              break;
            }
          } catch (error) {
            // Can't access iframe
          }
        }
      }

      // Scroll element into view
      this.targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    },

    /**
     * Check if element is currently in viewport
     */
    isElementInViewport() {
      if (!this.targetElement) return false;

      // Use getAbsoluteBounds to handle iframe elements
      const getAbsoluteBounds = (element) => {
        if (!element) return null;
        let rect = element.getBoundingClientRect();
        let top = rect.top;
        let left = rect.left;

        let currentElement = element;
        let ownerDoc = element.ownerDocument;
        while (ownerDoc !== document) {
          const iframes = document.querySelectorAll('iframe');
          for (const iframe of iframes) {
            try {
              if (iframe.contentDocument === ownerDoc) {
                const iframeRect = iframe.getBoundingClientRect();
                top += iframeRect.top;
                left += iframeRect.left;
                ownerDoc = iframe.ownerDocument;
                continue;
              }
            } catch (error) {
              break;
            }
          }
          break;
        }
        return {
          top,
          left,
          width: rect.width,
          height: rect.height,
          bottom: top + rect.height,
          right: left + rect.width,
        };
      };

      const bounds = getAbsoluteBounds(this.targetElement);
      return (
        bounds.top >= 0 &&
        bounds.left >= 0 &&
        bounds.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        bounds.right <= (window.innerWidth || document.documentElement.clientWidth)
      );
    },

    /**
     * Setup ResizeObserver to track element size/position changes
     */
    setupResizeObserver() {
      if (!window.ResizeObserver || !this.targetElement) return;

      this.resizeObserver = new ResizeObserver(() => {
        this.updateElementBounds();
      });

      this.resizeObserver.observe(this.targetElement);

      // Watch for DOM mutations on the element
      if (this.targetElement.parentElement) {
        const mutationObserver = new MutationObserver(() => {
          this.updateElementBounds();
        });

        mutationObserver.observe(this.targetElement.parentElement, {
          childList: true,
          subtree: true,
        });
      }
    },

    /**
     * Handle window resize events
     */
    handleWindowResize() {
      this.viewportDimensions = getViewportDimensions();
      this.updateElementBounds();
    },

    /**
     * Close overlay
     */
    closeOverlay() {
      if (this.closeOnBackdropClick) {
        this.showOverlay = false;
        this.$emit('close');
      }
    },

    /**
     * Handle enter transition
     */
    onEnter(el) {
      el.style.opacity = '0';
      el.style.transform = 'scale(0.95)';

      this.$nextTick(() => {
        el.style.transition = `all ${ANIMATION.DURATION}ms ${ANIMATION.EASING}`;
        el.style.opacity = '1';
        el.style.transform = 'scale(1)';
      });
    },

    /**
     * Handle leave transition
     */
    onLeave(el) {
      el.style.opacity = '0';
      el.style.transform = 'scale(0.95)';
    },

    /**
     * Inject CSS animations
     */
    injectAnimationStyles() {
      if (document.getElementById('demo-overlay-animations')) return;

      const style = document.createElement('style');
      style.id = 'demo-overlay-animations';
      style.innerHTML = `
        @keyframes demo-highlight-pulse {
          0% {
            box-shadow: 0 0 20px rgba(255, 215, 0, 0.6);
          }
          50% {
            box-shadow: 0 0 30px rgba(255, 215, 0, 0.9);
          }
          100% {
            box-shadow: 0 0 20px rgba(255, 215, 0, 0.6);
          }
        }

        .demo-overlay-content-enter-active,
        .demo-overlay-content-leave-active {
          transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
        }

        .demo-overlay-content-enter-from,
        .demo-overlay-content-leave-to {
          opacity: 0;
          transform: scale(0.95);
        }
      `;

      document.head.appendChild(style);
    },

    /**
     * Cleanup resources
     */
    cleanup() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      window.removeEventListener('resize', this.handleWindowResize);
    },
  },
};
</script>

<style scoped lang="scss">
.demo-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 999;
  pointer-events: none;

  &__backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 999;
    cursor: pointer;
    animation: demo-fade-in 300ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }

  &__container {
    position: fixed;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 998;
  }

  &__highlight {
    box-sizing: border-box;
  }

  &__tooltip {
    background-color: #1e1e2e;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
    padding: 16px;
    font-size: 14px;
    line-height: 1.6;
    color: #e0e0e0;
    pointer-events: auto;
    position: relative;

    &--top {
      transform-origin: center bottom;
    }

    &--bottom {
      transform-origin: center top;
    }

    &--left {
      transform-origin: right center;
    }

    &--right {
      transform-origin: left center;
    }
  }

  &__arrow {
    position: absolute;
    width: 12px;
    height: 12px;
    background-color: #1e1e2e;
    border-radius: 2px;
    transform: rotate(45deg);

    .demo-overlay__tooltip--top & {
      bottom: -6px;
      left: 50%;
      margin-left: -6px;
    }

    .demo-overlay__tooltip--bottom & {
      top: -6px;
      left: 50%;
      margin-left: -6px;
    }

    .demo-overlay__tooltip--left & {
      right: -6px;
      top: 50%;
      margin-top: -6px;
    }

    .demo-overlay__tooltip--right & {
      left: -6px;
      top: 50%;
      margin-top: -6px;
    }
  }

  &__content {
    position: relative;
    z-index: 1;
  }

  &__text {
    margin: 0;
    padding: 0;
    font-weight: 500;
  }
}

@keyframes demo-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.demo-overlay-content-enter-active,
.demo-overlay-content-leave-active {
  transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
}

.demo-overlay-content-enter-from,
.demo-overlay-content-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>
